/**
 * 🟦 V11.1 — Lyon Multi-Step Memory.
 *
 * Singleton in-memory (NO localStorage) que recuerda los últimos N nodos
 * sobre los que Lyon o el usuario actuaron durante la sesión. Habilita
 * conversación multi-paso ("ahora hacelo más grande", "ahora añadile
 * sombra") sin que el usuario tenga que re-seleccionar o re-describir
 * el nodo.
 *
 * Invariantes:
 *  - Reset automático al cambiar de app (UiBuilderPage llama
 *    `resetMemory()` al cambiar appId).
 *  - Reset automático al seleccionar un nodo distinto (la cadena queda
 *    asociada a UN solo nodo: si seleccionás otro, empezás chain nuevo).
 *  - lastScreenshot se invalida si el nodo cambia (nunca se usa con un
 *    nodeId distinto al actual — el chequeo es por uib_id).
 *  - No mezcla memoria entre apps (resetMemory limpia todo).
 *  - lastPatches/lastActions tienen cap=5 — FIFO drop del más viejo.
 */

const MAX_HISTORY = 5;

// Estado vivo del singleton. Cualquier mutación va por las funciones
// exportadas — nunca leer/escribir este objeto directamente desde fuera.
const state = {
  appId: null,
  lastNodeId: null,
  lastNodeContext: null,
  lastScreenshot: null,
  lastPatches: [],   // [{patch, ts, instruction}]
  lastActions: [],   // [{text, ts}]
  stylePack: null,   // 🟦 V12.1 — { id, version, appliedAt }
};

// ─── Subscriptores para que UI components reaccionen a cambios ──────────
const subscribers = new Set();
function emit() {
  // Snapshot inmutable defensivo para que React detecte el cambio.
  const snapshot = {
    appId: state.appId,
    lastNodeId: state.lastNodeId,
    lastNodeContext: state.lastNodeContext ? { ...state.lastNodeContext } : null,
    lastScreenshot: state.lastScreenshot,
    lastPatches: state.lastPatches.slice(),
    lastActions: state.lastActions.slice(),
    stylePack: state.stylePack ? { ...state.stylePack } : null,
  };
  for (const cb of subscribers) {
    try { cb(snapshot); } catch { /* ignore */ }
  }
}

/** Subscribirse a cambios. Devuelve función de unsubscribe. */
export function subscribeMemory(cb) {
  if (typeof cb !== 'function') return () => {};
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/** Snapshot síncrono de la memoria actual (sólo lectura). */
export function getMemorySnapshot() {
  return {
    appId: state.appId,
    lastNodeId: state.lastNodeId,
    lastNodeContext: state.lastNodeContext ? { ...state.lastNodeContext } : null,
    lastScreenshot: state.lastScreenshot,
    lastPatches: state.lastPatches.slice(),
    lastActions: state.lastActions.slice(),
    stylePack: state.stylePack ? { ...state.stylePack } : null,
  };
}

/**
 * 🟦 V12.1 — Registra el style pack aplicado actualmente.
 * Permite a Lyon recordar "qué estética está vigente" para futuros
 * prompts contextuales ("ahora hacelo más brutalista", "menos neón", …).
 */
export function setStylePack({ id, version } = {}) {
  if (!id) return;
  state.stylePack = {
    id: String(id),
    version: String(version || '1.0.0'),
    appliedAt: Date.now(),
  };
  emit();
}

/** Limpia toda la memoria. Lo llama UiBuilderPage al cambiar de appId. */
export function resetMemory(newAppId = null) {
  state.appId = newAppId;
  state.lastNodeId = null;
  state.lastNodeContext = null;
  state.lastScreenshot = null;
  state.lastPatches = [];
  state.lastActions = [];
  state.stylePack = null;
  emit();
}

/** Establece el appId actual; si cambia respecto al previo → resetMemory. */
export function setActiveApp(appId) {
  if (!appId) return;
  if (state.appId !== appId) {
    resetMemory(appId);
  }
}

/**
 * Registra el nodo actualmente seleccionado en el Builder.
 * Si el nodo es DISTINTO al previo, limpia patches/actions/screenshot
 * (cada chain de conversación pertenece a UN nodo).
 */
export function rememberSelection(nodeContext, screenshotB64 = null) {
  if (!nodeContext || !nodeContext.uib_id) {
    // Selección limpia → mantenemos lastNode hasta que se aplique algo.
    return;
  }
  const newId = nodeContext.uib_id;
  if (state.lastNodeId !== newId) {
    // Chain rota: nuevo nodo seleccionado.
    state.lastNodeId = newId;
    state.lastNodeContext = { ...nodeContext };
    state.lastScreenshot = screenshotB64 || null;
    state.lastPatches = [];
    state.lastActions = [];
  } else {
    // Mismo nodo: actualizar el contexto (puede haber cambiado por
    // edición no-IA en el inspector) y refrescar screenshot si vino.
    state.lastNodeContext = { ...nodeContext };
    if (screenshotB64) state.lastScreenshot = screenshotB64;
  }
  emit();
}

/**
 * Registra una acción IA exitosa (patch aplicado).
 * Actualiza lastNodeContext con el nodo post-patch que el caller pase.
 */
export function rememberPatch({ instruction, patch, updatedNodeContext, screenshotB64 = null }) {
  if (!updatedNodeContext || !updatedNodeContext.uib_id) return;
  // Si el patch se aplicó a un nodo distinto al recordado, sobreescribir.
  if (state.lastNodeId !== updatedNodeContext.uib_id) {
    state.lastNodeId = updatedNodeContext.uib_id;
    state.lastPatches = [];
    state.lastActions = [];
  }
  state.lastNodeContext = { ...updatedNodeContext };
  if (screenshotB64) state.lastScreenshot = screenshotB64;
  if (Array.isArray(patch) && patch.length) {
    state.lastPatches.push({ patch, ts: Date.now(), instruction: instruction || '' });
    if (state.lastPatches.length > MAX_HISTORY) {
      state.lastPatches.splice(0, state.lastPatches.length - MAX_HISTORY);
    }
  }
  emit();
}

/** Registra que el usuario mandó un comando (texto). */
export function rememberUserAction(text) {
  const t = (text || '').trim();
  if (!t) return;
  state.lastActions.push({ text: t.slice(0, 240), ts: Date.now() });
  if (state.lastActions.length > MAX_HISTORY) {
    state.lastActions.splice(0, state.lastActions.length - MAX_HISTORY);
  }
  emit();
}

/**
 * Detecta referencias implícitas en español/inglés que sugieren que el
 * usuario está hablando de un nodo previo sin re-seleccionarlo.
 *
 * Devuelve `true` si el texto matchea cualquiera de los patrones.
 * Función pura, segura para llamar en hot paths.
 */
const IMPLICIT_PATTERNS = [
  // Español. ⚠️ NO terminar con `\b` después de chars acentuados (á, í, ú,
  // ñ, etc.) porque JS regex no los considera word-chars y `\b` no matchea
  // la transición. Usamos `(?=\s|$|[.,!?¿])` o cierre explícito.
  /\b(ese|esa|esos|esas)\s+(mism[oa]s?|element[oa]s?|botón|botones|texto|párrafo|div|card|tarjeta|nodo|nodos)?\b/i,
  /\b(el|la|los|las)\s+(anterior|anteriores|último|última|mismo|misma)(?=\s|$|[.,!?])/i,
  /\bcontin[uú][aá](?=\s|$|[.,!?])/i,
  /\bseg[uú]i(lo|la|los|las)?(?=\s|$|[.,!?])/i,
  /\bahora\s+(hac[eé]l[oa]|añad[íi]l[eé]|agregal[eé]|pon[eé]l[oa]|duplical[oa]|alinealo|mové|sumal[eé])(?=\s|$|[.,!?])/i,
  /\blo\s+mismo\s+(pero|que|también)?\b/i,
  /\bigual\s+que\s+(antes|el\s+anterior)\b/i,
  /\b(también|tambien|asimismo)\s+(hac[eé]|añad[íi]|pon[eé])/i,
  // Sin sujeto explícito + verbo imperativo (lo más común): "hacelo más X"
  /\b(hac[eé]l[oa]|añad[íi]l[eé]|agregal[eé]|pon[eé]l[oa]|duplical[oa]|alinealo|mové|sumal[eé])(?=\s|$|[.,!?])/i,
  // Inglés
  /\bthat\s+(same|one|element|button|node)\b/i,
  /\b(the\s+)?(previous|last|same)\s+(one|element|button|node)\b/i,
  /\b(also|now)\s+(make|add|set|change|move|put)\b/i,
  /\bkeep\s+going\b/i,
  /\bsame\s+(thing|element)\b/i,
];

export function hasImplicitReference(text) {
  const t = (text || '').trim();
  if (!t) return false;
  for (const re of IMPLICIT_PATTERNS) {
    if (re.test(t)) return true;
  }
  return false;
}
